import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
  Slider,
  Chip,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/en-gb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useIDP } from '../contexts/IDPContext';
import EditableItemList from '../../../MyActivities/MyCV/components/common/EditableItemList';
import { IDP_PRIORITIES, IDP_PRIORITY_OPTIONS, IDP_USAGE_STATUSES, IDP_USAGE_STATUS_OPTIONS, getUsageStatusLabel } from '../../../../constants/idpPriorities';
import { TRACKING_MODES } from '../../../../constants/trackingModes';
import { toPickerValue, formatDateOnly } from '../../../../utils/dateOnly';

const ActionsTab = () => {
  const {
    goalData,
    updateActions,
    readonly,
    ragStatuses,
    resourceTemplates,
  } = useIDP();

  const [expandedActionIndex, setExpandedActionIndex] = useState(null);

  if (!goalData) return null;

  const measurementType = goalData.measurementType;
  const actions = goalData.actions || [];

  // Get the RAG status with the lowest order as default
  const getDefaultRagStatus = () => {
    if (!ragStatuses || ragStatuses.length === 0) return { id: null, status: null };
    const sorted = [...ragStatuses].sort((a, b) => (a.order || 0) - (b.order || 0));
    return { id: sorted[0].id, status: sorted[0] };
  };

  const createNewAction = () => {
    const defaultRag = getDefaultRagStatus();
    return {
      id: `temp-${Date.now()}`,
      name: '',
      description: '',
      measurementDescription: '',
      deadlineAt: null,
      priority: IDP_PRIORITIES.LOW,
      currentProgress: 0,
      currentRagStatusId: defaultRag.id,
      currentRagStatus: defaultRag.status,
      completedAt: null,
      resources: [],
    };
  };

  const handleActionsChange = (newActions) => {
    updateActions(newActions);
  };

  const getCompletionIcon = (action) => {
    if (action.completedAt) {
      return <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ color: '#9ca3af', fontSize: 20 }} />;
  };

  const getRAGColor = (ragStatus) => {
    return ragStatus?.color || '#9ca3af';
  };

  const renderActionSummary = (action) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      {/* Show completion icon for checklist type */}
      {measurementType?.trackingMode === TRACKING_MODES.CHECKLIST && getCompletionIcon(action)}

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {action.name || 'Untitled Action'}
        </Typography>
        {action.deadlineAt && (
          <Typography variant="body2" color="text.secondary">
            Due: {formatDateOnly(action.deadlineAt)}
          </Typography>
        )}
      </Box>

      {/* Progress indicator for Progress type */}
      {measurementType?.trackingMode === TRACKING_MODES.PROGRESS && (
        <Chip
          label={`${action.currentProgress || 0}%`}
          size="small"
          sx={{
            backgroundColor: '#eff6ff',
            color: '#2563eb',
            fontWeight: 600,
          }}
        />
      )}

      {/* RAG indicator for RAG type */}
      {measurementType?.trackingMode === TRACKING_MODES.RAG && action.currentRagStatus && (
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: getRAGColor(action.currentRagStatus),
          }}
        />
      )}

      {action.resources?.length > 0 && (
        <Chip
          label={`${action.resources.length} resource${action.resources.length > 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );

  const renderActionEditForm = (action, onChange) => {
    const [expandedResourceIndex, setExpandedResourceIndex] = useState(null);

    const createNewResource = () => ({
      id: `temp-${Date.now()}`,
      name: '',
      details: '',
      usageStatus: IDP_USAGE_STATUSES.NOT_USED,
      developmentResourceTemplateId: null,
      resourceTemplate: null,
    });

    const renderResourceSummary = (resource) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {resource.name || 'Untitled Resource'}
          </Typography>
          {resource.resourceTemplate?.category && (
            <Typography variant="body2" color="text.secondary">
              Template: {resource.resourceTemplate.category}
            </Typography>
          )}
        </Box>
        {resource.usageStatus && (
          <Chip
            label={getUsageStatusLabel(resource.usageStatus)}
            size="small"
            variant="outlined"
            sx={{ ml: 2 }}
          />
        )}
      </Box>
    );

    const renderResourceEditForm = (resource, onResourceChange) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={resourceTemplates}
          getOptionLabel={(option) => option.category || ''}
          value={resource.resourceTemplate || null}
          onChange={(_, newValue) => {
            onResourceChange({
              ...resource,
              resourceTemplate: newValue,
              developmentResourceTemplateId: newValue?.id || null,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Resource Template"
              size="small"
              required
            />
          )}
          disabled={readonly}
          size="small"
        />

        <TextField
          label="Resource Name"
          value={resource.name || ''}
          onChange={(e) => onResourceChange({ ...resource, name: e.target.value })}
          size="small"
          fullWidth
          required
          disabled={readonly}
        />

        <TextField
          label="Details"
          value={resource.details || ''}
          onChange={(e) => onResourceChange({ ...resource, details: e.target.value })}
          size="small"
          fullWidth
          multiline
          rows={2}
          disabled={readonly}
        />

        <FormControl size="small" fullWidth>
          <InputLabel>Usage Status</InputLabel>
          <Select
            value={resource.usageStatus || IDP_USAGE_STATUSES.NOT_USED}
            onChange={(e) => onResourceChange({ ...resource, usageStatus: e.target.value })}
            label="Usage Status"
            disabled={readonly}
          >
            {IDP_USAGE_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Action Name"
          value={action.name || ''}
          onChange={(e) => onChange({ ...action, name: e.target.value })}
          fullWidth
          required
          disabled={readonly}
        />

        <TextField
          label="Description"
          value={action.description || ''}
          onChange={(e) => onChange({ ...action, description: e.target.value })}
          fullWidth
          multiline
          rows={2}
          disabled={readonly}
        />

        <TextField
          label="How to Measure Success"
          value={action.measurementDescription || ''}
          onChange={(e) => onChange({ ...action, measurementDescription: e.target.value })}
          fullWidth
          multiline
          rows={2}
          placeholder="Describe how success will be measured for this action..."
          disabled={readonly}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Deadline"
              value={toPickerValue(action.deadlineAt)}
              onChange={(newValue) => {
                if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                  onChange({ ...action, deadlineAt: newValue.format('YYYY-MM-DD') });
                } else if (newValue === null) {
                  onChange({ ...action, deadlineAt: null });
                }
              }}
              disabled={readonly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  inputProps: {
                    placeholder: 'DD/MM/YYYY'
                  }
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={action.priority || IDP_PRIORITIES.MEDIUM}
                onChange={(e) => onChange({ ...action, priority: e.target.value })}
                label="Priority"
                disabled={readonly}
              >
                {IDP_PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </LocalizationProvider>

        {/* Checklist type - completion toggle */}
        {measurementType?.trackingMode === TRACKING_MODES.CHECKLIST && (
          <Box sx={{
            p: 2,
            backgroundColor: '#f8fafc',
            borderRadius: 1,
            border: '1px solid #e2e8f0',
          }}>
            <FormControl fullWidth>
              <InputLabel>Completion Status</InputLabel>
              <Select
                value={action.completedAt ? 'completed' : 'pending'}
                onChange={(e) => {
                  const isCompleted = e.target.value === 'completed';
                  onChange({
                    ...action,
                    completedAt: isCompleted ? new Date().toISOString() : null,
                    currentProgress: isCompleted ? 100 : 0,
                  });
                }}
                label="Completion Status"
                disabled={readonly}
              >
                <MenuItem value="pending">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    Pending
                  </Box>
                </MenuItem>
                <MenuItem value="completed">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                    Completed
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Progress type - slider */}
        {measurementType?.trackingMode === TRACKING_MODES.PROGRESS && (
          <Box sx={{
            p: 2,
            backgroundColor: '#f8fafc',
            borderRadius: 1,
            border: '1px solid #e2e8f0',
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Progress: {action.currentProgress || 0}%
            </Typography>
            <Slider
              value={action.currentProgress || 0}
              onChange={(_, newValue) => onChange({ ...action, currentProgress: newValue })}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' },
              ]}
              disabled={readonly}
              sx={{ color: '#2563eb' }}
            />
          </Box>
        )}

        {/* RAG type - status selector */}
        {measurementType?.trackingMode === TRACKING_MODES.RAG && (
          <Box sx={{
            p: 2,
            backgroundColor: '#f8fafc',
            borderRadius: 1,
            border: '1px solid #e2e8f0',
          }}>
            <FormControl fullWidth>
              <InputLabel>RAG Status</InputLabel>
              <Select
                value={action.currentRagStatusId || action.currentRagStatus?.id || ''}
                onChange={(e) => {
                  const statusId = e.target.value;
                  const status = ragStatuses.find(s => s.id === statusId);
                  onChange({
                    ...action,
                    currentRagStatusId: statusId,
                    currentRagStatus: status,
                  });
                }}
                label="RAG Status"
                disabled={readonly}
              >
                {ragStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: getRAGColor(status),
                        }}
                      />
                      {status.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Action Resources */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Action Resources
          </Typography>
          <EditableItemList
            items={action.resources || []}
            setItems={(newResources) => onChange({ ...action, resources: newResources })}
            expandedIndex={expandedResourceIndex}
            setExpandedIndex={setExpandedResourceIndex}
            createNewItem={createNewResource}
            renderSummary={renderResourceSummary}
            renderEditForm={renderResourceEditForm}
            addButtonText="Add Resource"
            emptyState={{
              title: 'No resources',
              description: 'Add resources for this action.',
            }}
            itemName="resource"
            readonly={readonly}
          />
        </Box>
      </Box>
    );
  };

  // Calculate summary stats
  const totalActions = actions.length;
  const completedActions = actions.filter(a => a.completedAt).length;
  const avgProgress = totalActions > 0
    ? Math.round(actions.reduce((sum, a) => sum + (a.currentProgress || 0), 0) / totalActions)
    : 0;

  return (
    <Box>
      {/* Summary Header */}
      {totalActions > 0 && (
        <Box sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#f8fafc',
          borderRadius: 1,
          border: '1px solid #e2e8f0',
        }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {totalActions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Actions
              </Typography>
            </Box>

            {/* Checklist type summary */}
            {measurementType?.trackingMode === TRACKING_MODES.CHECKLIST && (
              <Box>
                <Typography variant="h5" fontWeight={600} color="#16a34a">
                  {completedActions}/{totalActions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </Box>
            )}

            {/* Progress type summary */}
            {measurementType?.trackingMode === TRACKING_MODES.PROGRESS && (
              <Box>
                <Typography variant="h5" fontWeight={600} color="#2563eb">
                  {avgProgress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Progress
                </Typography>
              </Box>
            )}

            {/* RAG type summary */}
            {measurementType?.trackingMode === TRACKING_MODES.RAG && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {ragStatuses.map((ragStatus) => {
                  const count = actions.filter(a => a.currentRagStatus?.id === ragStatus.id).length;
                  return (
                    <Box key={ragStatus.id} sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: ragStatus.color || '#9ca3af',
                          }}
                        />
                        <Typography variant="h6" fontWeight={600}>
                          {count}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {ragStatus.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Actions List */}
      <EditableItemList
        items={actions}
        setItems={handleActionsChange}
        expandedIndex={expandedActionIndex}
        setExpandedIndex={setExpandedActionIndex}
        createNewItem={createNewAction}
        renderSummary={renderActionSummary}
        renderEditForm={renderActionEditForm}
        addButtonText="Add Action"
        emptyState={{
          title: 'No actions defined',
          description: 'Add actions to break down this development goal into manageable steps.',
        }}
        itemName="action"
        readonly={readonly}
      />
    </Box>
  );
};

export default ActionsTab;
